/*
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { Popover } from "bootstrap"
import { CSSProperties, FC, useEffect, useRef } from "react"

interface Props {
  readonly content: string
  readonly htmlContent?: boolean
  readonly style?: CSSProperties
}

// AIDEV-NOTE: (#536) `dispose()` unbinds only Bootstrap's own `.bs.popover` namespace
// via EventHandler - `BaseComponent.dispose()` (bootstrap.esm.js:667) - so the raw
// `addEventListener` below outlives every disposal and cannot be removed afterwards.
// Tracking which triggers already carry it keeps setup idempotent even across the
// dispose/re-create cycle a content change performs. A WeakSet, so an unmounted trigger
// stays collectable.
const triggersWithOutsideClickDismissal = new WeakSet<Element>()

// AIDEV-NOTE: (#545) Keyed per trigger rather than captured in a closure so that
// teardownPopover() - called from InfoPopover's unmount/content-change cleanup, which
// never sees `shown.bs.popover` fire - can still reach and abort whichever dismissal
// listener is currently live for that trigger. A WeakMap, so an unmounted trigger stays
// collectable even if teardown is skipped.
const activeDismissalControllers = new WeakMap<Element, AbortController>()

/**
 * Hide the popover when the user clicks outside both it and its trigger.
 *
 * Registered at most once per trigger element, however many times setup runs.
 */
const registerOutsideClickDismissal = (popoverElement: Element) => {
  if (triggersWithOutsideClickDismissal.has(popoverElement)) {
    return
  }
  triggersWithOutsideClickDismissal.add(popoverElement)

  popoverElement.addEventListener("shown.bs.popover", () => {
    // AIDEV-NOTE: (#544) Resolved through the trigger's own `aria-describedby` - the
    // link Bootstrap itself maintains (set in show(), removed in hide()) - rather than
    // `document.querySelector(".popover")`, which returns the first `.popover` in
    // document order and cannot tell one trigger's tip from another's once two coexist
    // (e.g. clicking trigger B while A's hide is still deferred through its transition
    // queue).
    const tipId = popoverElement.getAttribute("aria-describedby")
    const shownPopover = tipId ? document.getElementById(tipId) : null

    if (shownPopover) {
      // AIDEV-NOTE: (#545) One AbortController per shown cycle, aborted from the same
      // cleanup that calls dispose() (teardownPopover) or from hidden.bs.popover.
      // `hidden` does not reliably follow hide(): hide()'s completion callback early-returns
      // without emitting it whenever a trigger has gone active again in the meantime
      // (the `_isWithActiveTrigger()` guard in `Tooltip.hide()`'s `complete` callback,
      // bootstrap.esm.js:3357-3360), which is what reopening during the fade does. So a cycle
      // can end with its controller still live, and the incoming cycle must abort it rather
      // than overwrite it - otherwise the displaced controller is unreachable through both
      // this map and teardownPopover, stranding its document listener for good. That
      // stranded listener still holds the *previous* tip, so it re-answers "is this click
      // inside the popover?" against a detached element and dismisses the live popover on a
      // click into its own body - #544's symptom, reached by a different route.
      // The document listener is removed via an explicit removeEventListener call triggered
      // off the "abort" event, rather than passed `{ signal }` directly. That indirection
      // buys testability, not correctness: trackNativeListeners (nativeListenerTracking.ts)
      // spies on the removeEventListener *method*, and a signal-based removal runs through
      // DOM's internal removal steps instead of calling it, so #545's regression test could
      // not observe the removal at all. Real devtools are not blind to it - getEventListeners()
      // reads the listener registry, which a signal removal does update - so `{ signal }`
      // would be equally leak-free in a browser, just unmeasurable by this suite.
      const controller = new AbortController()
      activeDismissalControllers.get(popoverElement)?.abort()
      activeDismissalControllers.set(popoverElement, controller)

      // When clicking anywhere in the document
      const documentClickHandler = (event: MouseEvent) => {
        // Check if click is outside both the trigger and the popover
        const clickedElement = event.target as Node
        const isClickInsidePopover = shownPopover.contains(clickedElement)
        const isClickOnTrigger = popoverElement.contains(clickedElement)

        // Only hide if clicked outside both elements
        if (!isClickInsidePopover && !isClickOnTrigger) {
          // Resolved per click rather than captured: this listener outlives any one
          // instance, so a captured reference would go stale after a content change.
          Popover.getInstance(popoverElement)?.hide()
        }
      }

      // Add the document click handler
      document.addEventListener("click", documentClickHandler)
      controller.signal.addEventListener("abort", () => {
        document.removeEventListener("click", documentClickHandler)
      })

      // Remove the event listener when popover is hidden
      popoverElement.addEventListener(
        "hidden.bs.popover",
        () => {
          activeDismissalControllers.delete(popoverElement)
          controller.abort()
        },
        { once: true, signal: controller.signal }
      )
    }
  })
}

// AIDEV-NOTE: (#545) The only teardown path that reaches a dismissal listener left
// active by an *open* popover being unmounted or rebuilt for changed content - neither
// goes through hide(), so hidden.bs.popover never fires to remove it itself.
// Popover instance whose own methods (e.g. dispose()) are inherently mutating.
// eslint-disable-next-line @typescript-eslint/prefer-readonly-parameter-types
const teardownPopover = (popoverElement: Element, popover: Popover) => {
  activeDismissalControllers.get(popoverElement)?.abort()
  activeDismissalControllers.delete(popoverElement)
  popover.dispose()
}

// AIDEV-NOTE: (#536) `new Popover(el)` on an element that already has one is silently
// destructive: `Data.set()` (bootstrap.esm.js:21-35) overwrites the registry entry at :34
// instead of erroring. Its one-instance-per-element guard (:29-33) is no protection here -
// it fires only for a *different* key on the same element, and two Popovers share a
// `DATA_KEY`, so `instanceMap.has(key)` is true and the overwrite proceeds. The displaced
// instance is then unreachable through getInstance() and can never be disposed. That leaked
// one orphaned instance and one click handler per call on every trigger in the document,
// unrecoverable short of a page reload.
// getOrCreateInstance holds it to one instance per element, which is what makes
// setupPopovers() safe to call repeatedly.
// AIDEV-NOTE: (#634) `animation: false` is load-bearing, not cosmetic. Bootstrap's
// `hide()`/`show()` defer their completion callback through `executeAfterTransition`
// (bootstrap.esm.js:251-274), which arms a `setTimeout` fallback that nothing - not
// `dispose()`, not `clearTimeout(this._timeout)` - ever cancels. Unmounting during the
// fade therefore left that timer to fire against an instance whose properties
// `super.dispose()` (bootstrap.esm.js:665) had already nulled, and `complete()` threw on
// `Object.values(this._activeTrigger)`. With animation off, `_isAnimated()` is
// permanently false - the tip never receives the `fade` class (bootstrap.esm.js:3397)
// that is its only other input - so `executeAfterTransition` takes its synchronous early
// return and no callback is ever left in flight to strand. Restoring the fade
// reintroduces the crash.
//
// The alternative was to flush the pending transition before `dispose()`. Reaching the
// tip is not what rules it out - `aria-describedby` still resolves it mid-hide, since
// `complete()` is what clears that attribute (bootstrap.esm.js:3364) and is precisely
// the callback that has not run yet. What rules it out is the flush itself: it works
// only by synthesising a `transitionend` that a handler private to
// `executeAfterTransition` happens to be listening for, which is a deeper coupling to
// Bootstrap's internals than declining to animate at all.
const setupPopover = (popoverElement: Element): Popover => {
  const popover = Popover.getOrCreateInstance(popoverElement, {
    trigger: "click", // Change to click instead of hover
    container: "body",
    animation: false,
  })

  registerOutsideClickDismissal(popoverElement)

  return popover
}

export const setupPopovers = () => {
  const popoverTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="popover"]'))
  popoverTriggerList.forEach(popoverElement => setupPopover(popoverElement))
}

export const InfoPopover: FC<Props> = ({ content, htmlContent, style }) => {
  const popoverRef = useRef<HTMLElement>(null)

  // AIDEV-NOTE: (#536) Keyed on the content props because Bootstrap resolves
  // `data-bs-content`/`data-bs-html` once, at construction. Before setupPopover became
  // idempotent, a passing setupPopovers() sweep happened to rebuild the instance and
  // pick up changed content; disposing and re-creating here replaces that accident with
  // something deliberate. Matters for the one call site whose content is dynamic
  // (FinalValueChart.tsx:197-203).
  useEffect(() => {
    const popoverElement = popoverRef.current
    if (popoverElement) {
      const popoverInstance = setupPopover(popoverElement)

      // Cleanup on unmount, or before re-creating with changed content
      return () => {
        teardownPopover(popoverElement, popoverInstance)
      }
    }
  }, [content, htmlContent])

  return (
    <i
      ref={popoverRef}
      className="fas fa-info-circle ps-2 fs-6"
      data-bs-toggle="popover"
      data-bs-html={htmlContent ? "true" : "false"}
      data-bs-content={content}
      style={style}
    ></i>
  )
}
